import {
  COMPLETION_DATE_FIELDS,
  COMPLETION_STATE,
  formatCompletionDate,
  getCompletionState,
  getPendingCompletionLabels,
  getVisitCompletionDate as getVisitCompletionDay,
} from '../../../utils/visitCompletion';

// Date maths of the personal calendar. The rules are the ones the calendar
// always used; they only moved here so the grid, the day list and the Excel
// export share them.

const DAY_MS = 1000 * 60 * 60 * 24;

/** Start of the day, local time. */
export const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/** 'yyyy-mm-dd' of a local date. */
export const dateKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const isSameDay = (a, b) => a && b && normalizeDate(a).getTime() === normalizeDate(b).getTime();

/**
 * Visit window: `period` is "-X,+Y" (days before / after the visit date) in
 * new records; old records hold one number (positive: that many days from
 * the visit date on, negative: up to the visit date).
 */
export const visitWindow = (visit) => {
  const firstVisitDate = normalizeDate(new Date(visit.firstVisitDate));
  const period = visit.period || '0';
  let start;
  let end;

  if (typeof period === 'string' && period.includes(',')) {
    const parts = period.split(',');
    const beforeDays = Math.abs(parseInt(parts[0], 10) || 0);
    const afterDays = Math.abs(parseInt(parts[1], 10) || 0);
    start = new Date(firstVisitDate);
    start.setDate(firstVisitDate.getDate() - beforeDays);
    end = new Date(firstVisitDate);
    end.setDate(firstVisitDate.getDate() + afterDays);
  } else {
    const periodNum = typeof period === 'number' ? period : parseInt(period, 10) || 1;
    if (periodNum >= 0) {
      start = new Date(firstVisitDate);
      end = new Date(firstVisitDate);
      end.setDate(firstVisitDate.getDate() + (periodNum - 1));
    } else {
      start = new Date(firstVisitDate);
      start.setDate(firstVisitDate.getDate() + periodNum + 1);
      end = new Date(firstVisitDate);
    }
  }
  return { start, end, firstVisitDate };
};

/** Days from the visit date: 0 on it, negative before, positive after. */
export const visitDayOffset = (visit, date) => Math.round(
  (normalizeDate(date) - normalizeDate(new Date(visit.firstVisitDate))) / DAY_MS
);

/** Which day of a job this is (1 = start day). */
export const jobDayNumber = (job, date) => Math.ceil(
  Math.abs(normalizeDate(date) - normalizeDate(new Date(job.startDate))) / DAY_MS
) + 1;

export const isJobOnDay = (job, date) => {
  const day = normalizeDate(date);
  return day >= normalizeDate(new Date(job.startDate)) && day <= normalizeDate(new Date(job.endDate));
};

/**
 * Visit state on a given day:
 *  completedDay   – the day it was actually done
 *  windowClosed   – completed, another day of its window
 *  completed      – completed, no completion date recorded (old data)
 *  overdue        – not completed, 72 hours past its end
 *  inProgress     – it's the visit date, or the visit date has passed
 *  planned        – the visit date is ahead
 */
export const visitDayState = (visit, date, today = normalizeDate(new Date())) => {
  const completion = getCompletionState(visit, date);
  if (completion === COMPLETION_STATE.COMPLETED_DAY) return 'completedDay';
  if (completion === COMPLETION_STATE.WINDOW_CLOSED) return 'windowClosed';
  if (completion === COMPLETION_STATE.COMPLETED_UNKNOWN) return 'completed';

  const visitDate = normalizeDate(new Date(visit.firstVisitDate));
  // Eski sayısal `period` ile bitiş günü; "-X,+Y" kayıtlarda vizit günü
  const period = visit.period || 1;
  const visitEndDate = new Date(visitDate);
  if (period >= 0) visitEndDate.setDate(visitDate.getDate() + (period - 1));
  // Bitişten sonra 72 saat tolerans
  const deadline = new Date(visitEndDate);
  deadline.setHours(23, 59, 59, 999);
  deadline.setTime(deadline.getTime() + 72 * 60 * 60 * 1000);

  if (deadline < new Date()) return 'overdue';
  if (visitDate > today) return 'planned';
  return 'inProgress';
};

/** Job state: done, today, ahead or behind. */
export const jobState = (job, today = normalizeDate(new Date())) => {
  if (job.status) return 'done';
  const start = normalizeDate(new Date(job.startDate));
  if (start.getTime() === today.getTime()) return 'today';
  return start > today ? 'planned' : 'overdue';
};

/** Keys of the D-section dates still missing on a completed visit. */
export const pendingCompletionKeys = (visit) => {
  const labels = getPendingCompletionLabels(visit);
  return COMPLETION_DATE_FIELDS.filter(({ label }) => labels.includes(label)).map(({ key }) => key);
};

/**
 * One-line status of a visit on a day, translated. Same content the calendar
 * tooltip always had (completion date, closed window, missing dates).
 */
export const visitStatusLine = (t, visit, date, { whole = false } = {}) => {
  const label = (key, vars) => t(`profilePage.calendar.status.${key}`, vars);
  if (visit.screenFail) return label('screenFail');
  if (!visit.status) {
    return normalizeDate(new Date(visit.firstVisitDate)) < normalizeDate(new Date()) ? label('notCompleted') : label('ongoing');
  }
  const pending = pendingCompletionKeys(visit).map((key) => t(`profilePage.calendar.pending.${key}`));
  const pendingNote = pending.length > 0 ? label('missingSuffix', { items: pending.join(', ') }) : '';
  const formatted = formatCompletionDate(visit, { withTime: true });
  if (!formatted) return label('completedNoDate') + pendingNote;
  // Pencerenin tamamı için (çubuk): yalnızca ne zaman tamamlandığı
  if (whole) return label('completedOn', { date: formatted }) + pendingNote;
  return (getCompletionState(visit, date) === COMPLETION_STATE.COMPLETED_DAY
    ? label('completedThisDay', { date: formatted })
    : label('completedWindowClosed', { date: formatted })) + pendingNote;
};

/**
 * Is this the visit's own day (its visit date, or the day it was done)?
 * The other days of its window only mean "it can still be done then".
 */
export const isVisitOnDay = (visit, date) => visitDayOffset(visit, date) === 0
  || getCompletionState(visit, date) === COMPLETION_STATE.COMPLETED_DAY;

/**
 * State of a visit as a whole (for its window bar): screen fail, completed,
 * or — by its visit day — overdue, in progress or planned.
 */
export const visitOverallState = (visit) => {
  if (visit.screenFail) return 'screenFail';
  if (visit.status) return 'completed';
  return visitDayState(visit, normalizeDate(new Date(visit.firstVisitDate)));
};

/**
 * What the month grid draws: one item per visit (its window, or only its
 * visit day when windows are hidden) and per job (start to end).
 */
export const buildMonthItems = ({ visits, jobs, showWindows }) => {
  const items = [];
  visits.forEach((visit) => {
    if (!visit.firstVisitDate) return;
    const { start, end, firstVisitDate } = visitWindow(visit);
    if (Number.isNaN(firstVisitDate.getTime())) return;
    const completion = visit.status ? getVisitCompletionDay(visit) : null;
    items.push({
      kind: 'visit',
      key: `v-${visit.id}`,
      item: visit,
      start: showWindows && !Number.isNaN(start.getTime()) ? start : firstVisitDate,
      end: showWindows && !Number.isNaN(end.getTime()) ? end : firstVisitDate,
      target: firstVisitDate,
      completion,
    });
  });
  jobs.forEach((job) => {
    const start = normalizeDate(new Date(job.startDate));
    const end = normalizeDate(new Date(job.endDate || job.startDate));
    if (Number.isNaN(start.getTime())) return;
    items.push({
      kind: 'job',
      key: `j-${job.id}`,
      item: job,
      start,
      end: Number.isNaN(end.getTime()) || end < start ? start : end,
      target: start,
      completion: null,
    });
  });
  return items;
};

const dayIndex = (from, to) => Math.round((normalizeDate(to) - normalizeDate(from)) / DAY_MS);

/**
 * One week row of the month grid: each item that touches the week becomes a
 * segment over its days (clipped to the shown month), stacked in lanes so
 * overlapping segments don't cover each other. `week` holds 7 Dates or nulls.
 */
export const layoutWeek = (items, week) => {
  const columns = week.map((date, index) => (date ? index : null)).filter((index) => index !== null);
  if (columns.length === 0) return { segments: [], laneCount: 0 };
  const firstCol = columns[0];
  const lastCol = columns[columns.length - 1];
  const weekStart = week[firstCol];
  const weekEnd = week[lastCol];

  const segments = items
    .filter((entry) => entry.start <= weekEnd && entry.end >= weekStart)
    .map((entry) => {
      const from = entry.start < weekStart ? weekStart : entry.start;
      const to = entry.end > weekEnd ? weekEnd : entry.end;
      const startCol = firstCol + dayIndex(weekStart, from);
      const endCol = firstCol + dayIndex(weekStart, to);
      const markCol = entry.target >= from && entry.target <= to ? firstCol + dayIndex(weekStart, entry.target) : null;
      const doneCol = entry.completion && entry.completion >= from && entry.completion <= to ? firstCol + dayIndex(weekStart, entry.completion) : null;
      return {
        ...entry,
        startCol,
        endCol,
        markCol,
        doneCol,
        continuesLeft: entry.start < weekStart,
        continuesRight: entry.end > weekEnd,
      };
    })
    // Uzun ve erken başlayanlar üstte; aynı yerde vizitler işlerden önce
    .sort((a, b) => a.startCol - b.startCol
      || (b.endCol - b.startCol) - (a.endCol - a.startCol)
      || (a.kind === b.kind ? 0 : (a.kind === 'visit' ? -1 : 1))
      || a.target - b.target);

  const laneEnds = [];
  segments.forEach((segment) => {
    let lane = laneEnds.findIndex((end) => end < segment.startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(segment.endCol);
    } else {
      laneEnds[lane] = segment.endCol;
    }
    // eslint-disable-next-line no-param-reassign
    segment.lane = lane;
  });

  return { segments, laneCount: laneEnds.length };
};

/** Cells of a month, Monday first: `null` for the blanks before day 1. */
export const monthCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const leading = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: leading }, () => null);
  for (let day = 1; day <= days; day += 1) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/**
 * Visits per day of the shown month ('yyyy-mm-dd' → visits), so each cell
 * doesn't scan every visit.
 */
export const visitsByDay = (visits, monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const map = new Map();
  visits.forEach((visit) => {
    if (!visit.firstVisitDate) return;
    const { start, end } = visitWindow(visit);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    const from = start > monthStart ? start : monthStart;
    const to = end < monthEnd ? end : monthEnd;
    const cursor = new Date(from);
    let guard = 0;
    while (cursor <= to && guard < 400) {
      const key = dateKey(cursor);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(visit);
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  });
  return map;
};
